import React from 'react';
import { InfoBox } from './InfoBox';
import { PullQuote } from './PullQuote';
import { IngredientGrid } from './IngredientGrid';
import { CtaBox } from './CtaBox';
import { Timeline } from './Timeline';
import { ReviewCards } from './ReviewCards';
import { DarkCta } from './DarkCta';
export function BodyContent() {
  return <article className="max-w-[680px] mx-auto px-[18px] sm:px-7 pt-[52px] pb-[88px] text-[18px] leading-[1.85] font-serif text-text" data-id="element-7">
      <p className="mb-[1.6em]" data-id="element-8">
        Es begann mit einem harmlosen Kratzen an der Schulter. Leni war damals
        zwei Jahre alt, es war Frühling, und ich dachte: Pollen. Das gibt sich.
        Es gab sich nicht wieder.
      </p>

      <p className="mb-[1.6em]" data-id="element-9">
        Was folgte, waren über zwei Jahre, in denen meine Hündin täglich
        kratzte, sich scheuerte, leckte. Ihre Pfoten waren rötlich verfärbt, die
        Haut an den Achseln immer wieder gereizt. Im Sommer wurde es schlimmer,
        aber auch im Winter verschwand der Juckreiz nie ganz.
      </p>

      <p className="mb-[1.6em]" data-id="element-10">
        Ich habe wirklich alles versucht. Kortison-Präparate vom Tierarzt.
        Verschiedene Shampoos. Hühnchenfleisch aus dem Futterplan gestrichen,
        dann Rind, dann Getreide. Ein teures hypoallergenes
        Spezialtrocken­futter. Antiparasitenmittel. Fettsäurekapseln. Manches
        half ein bisschen, für ein paar Wochen. Dann kratzte Leni wieder.
      </p>

      <h2 className="font-serif text-[1.45rem] font-bold text-text mt-[2.6em] mb-[0.8em] leading-[1.3]" data-id="element-11">
        Die Frage, die alles verändert hat
      </h2>

      <p className="mb-[1.6em]" data-id="element-12">
        Der Wendepunkt kam durch eine Tierärztin, die ich zufällig auf einem
        Hundespaziergang kennenlernte. Nachdem ich ihr Lenies Geschichte erzählt
        hatte, fragte sie mich etwas, das ich bis dahin noch nie gehört hatte:
        „Habt ihr mal die Darmflora angeschaut?"
      </p>

      <p className="mb-[1.6em]" data-id="element-13">
        Ich verstand die Frage zunächst nicht. Lenies Problem war doch die Haut
        – nicht der Magen. Aber die Ärztin erklärte mir, was ich seitdem immer
        wieder bestätigt sehe: Ein Ungleichgewicht im Darmmikrobiom kann das
        Immunsystem dauerhaft in einen Alarmzustand versetzen. Und ein
        übersensibles Immunsystem reagiert auf alles – Pollen, Futtermittel,
        Umweltreize. Das zeigt sich dann auf der Haut als chronischer Juckreiz.
      </p>

      <InfoBox data-id="element-14" />

      <p className="mb-[1.6em]" data-id="element-15">
        Das war mein Aha-Moment. Ich hatte drei Jahre lang die Haut behandelt –
        dabei hätte ich beim Darm anfangen sollen.
      </p>

      <PullQuote data-id="element-16" />

      <h2 className="font-serif text-[1.45rem] font-bold text-text mt-[2.6em] mb-[0.8em] leading-[1.3]" data-id="element-17">
        Was ich dann ausprobiert habe
      </h2>

      <p className="mb-[1.6em]" data-id="element-18">
        Ich begann zu recherchieren, was gezielt die Darmflora von Hunden
        unterstützt. Dabei bin ich auf das{' '}
        <strong className="font-bold" data-id="element-19">
          Futtertopping Magen & Darm von Hunderunde
        </strong>{' '}
        gestoßen – ein rein pflanzliches Pulver aus acht Zutaten, das täglich
        über das normale Futter gegeben wird. Keine Tabletten, kein Umstellen
        des Futters, kein großes Aufwand.
      </p>

      <p className="mb-[1.6em]" data-id="element-20">Was drin ist, überzeugte mich sofort:</p>

      <IngredientGrid data-id="element-21" />

      <p className="mb-[1.6em]" data-id="element-22">
        Kein einziges tierisches Protein – was wichtig ist, denn tierische
        Proteine wie Rind oder Hühnchen sind häufige Allergieauslöser. Das
        Topping passte also sofort zu Lenies empfindlichem Immunsystem.
      </p>

      <CtaBox data-id="element-23" />

      <h2 className="font-serif text-[1.45rem] font-bold text-text mt-[2.6em] mb-[0.8em] leading-[1.3]" data-id="element-24">
        Was in den ersten Wochen passierte
      </h2>

      <p className="mb-[1.6em]" data-id="element-25">
        Ich war vorsichtig optimistisch – und vor allem vorbereitet darauf, dass
        es Zeit brauchen würde. Das Topping wird schrittweise eingeführt, weil
        sich der Darm langsam anpassen soll. Ich habe mich genau an die
        Empfehlung gehalten:
      </p>

      <Timeline data-id="element-26" />

      <p className="mb-[1.6em]" data-id="element-27">
        War das Topping allein dafür verantwortlich? Ich kann es nicht mit
        hundertprozentiger Sicherheit sagen. Aber es war die einzige Variable,
        die ich verändert hatte. Und nach über zwei Jahren ist das für mich
        Beweis genug.
      </p>

      <h2 className="font-serif text-[1.45rem] font-bold text-text mt-[2.6em] mb-[0.8em] leading-[1.3]" data-id="element-28">
        Was andere berichten
      </h2>

      <p className="mb-[1.6em]" data-id="element-29">
        Als ich anfing, mich durch die Bewertungen von Hunderunde zu lesen, fiel
        mir auf: Viele Hundebesitzer beschreiben genau dasselbe Muster – und
        viele erwähnen, dass sich neben den Magen-Darm-Symptomen auch
        Hautreizungen und Juckreiz gebessert haben.
      </p>

      <ReviewCards data-id="element-30" />

      <h2 className="font-serif text-[1.45rem] font-bold text-text mt-[2.6em] mb-[0.8em] leading-[1.3]" data-id="element-31">
        Mein ehrliches Fazit
      </h2>

      <p className="mb-[1.6em]" data-id="element-32">
        Ich bin keine Tierärztin und mache keine medizinischen Versprechen. Was
        ich sagen kann: Wenn dein Hund unter chronischem Juckreiz leidet und ihr
        schon vieles ausprobiert habt, lohnt es sich, den Blick nach innen zu
        richten. Zur Darmflora. Zum Immunsystem. Zu dem, was täglich in den Napf
        kommt.
      </p>

      <p className="mb-[1.6em]" data-id="element-33">
        Das Futtertopping Magen & Darm von Hunderunde hat für Leni einen echten
        Unterschied gemacht – nicht weil es ein Wundermittel ist, sondern weil
        es endlich beim richtigen Ansatzpunkt angesetzt hat. Und die 60-tägige
        Verträglichkeitsgarantie macht den Test komplett risikolos: Wenn es
        nichts bringt, gibt es das Geld zurück.
      </p>

      <p className="mb-[1.6em]" data-id="element-34">
        Ich wünschte, ich wäre drei Jahre früher darauf gekommen.
      </p>

      <DarkCta data-id="element-35" />

      <p className="font-sans text-[0.78rem] text-text-muted leading-[1.65] border-t border-border pt-6 mt-[3em]" data-id="element-36">
        <strong className="font-bold" data-id="element-37">Hinweis:</strong> Dieser Artikel ist ein
        Advertorial und enthält einen Affiliate-Link zu Hunderunde. Meine
        Erfahrungen sind meine eigenen und wurden nicht von Hunderunde
        beeinflusst. Das Topping ist kein Arzneimittel und ersetzt keine
        tierärztliche Behandlung. Bei anhaltenden Beschwerden bitte immer einen
        Tierarzt aufsuchen.
      </p>
    </article>;
}